import React, { Dispatch, createContext, useContext, useReducer, useMemo } from 'react'

export enum ConnectPanelActionType {
  SET_CONNECT_PANEL_OPEN = 'SET_CONNECT_PANEL_OPEN',
  SET_CONNECT_PANEL_DISABLED = 'SET_CONNECT_PANEL_DISABLED'
}

export type ConnectPanelState = {
  connectPanelOpen: boolean
  connectPanelDisabled: boolean
}

const ConnectPanelStateContext = createContext<ConnectPanelState | undefined>(undefined)

type Action =
  | { type: ConnectPanelActionType.SET_CONNECT_PANEL_OPEN; open: boolean }
  | { type: ConnectPanelActionType.SET_CONNECT_PANEL_DISABLED; disabled: boolean }

type TokensDataDispatch = Dispatch<Action>

const ConnectPanelDispatchContext = createContext<TokensDataDispatch | undefined>(undefined)

const initialConnectPanelState: ConnectPanelState = {
  connectPanelOpen: false,
  connectPanelDisabled: false
}

function connectPanelReducer(state: ConnectPanelState, action: Action): ConnectPanelState {
  switch (action.type) {
    case ConnectPanelActionType.SET_CONNECT_PANEL_OPEN:
      return {
        ...state,
        connectPanelOpen: action.open
      }
    case ConnectPanelActionType.SET_CONNECT_PANEL_DISABLED:
      return {
        ...state,
        connectPanelDisabled: action.disabled
      }
    default:
      throw new Error('Unhandled action')
  }
}

export function ConnectPanelProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(connectPanelReducer, initialConnectPanelState)

  return (
    <ConnectPanelDispatchContext.Provider value={dispatch}>
      <ConnectPanelStateContext.Provider value={useMemo(() => state, [state])}>
        {children}
      </ConnectPanelStateContext.Provider>
    </ConnectPanelDispatchContext.Provider>
  )
}

export function useConnectPanelState() {
  const state = useContext(ConnectPanelStateContext)
  if (!state) throw new Error('ConnectPanelProvider not found')
  return state
}

export function useConnectPanelDispatch() {
  const dispatch = useContext(ConnectPanelDispatchContext)
  if (!dispatch) throw new Error('ConnectPanelProvider not found')
  return dispatch
}

export const useConnectPanel = (): {
  setConnectPanelOpen: (open: boolean) => void
  setConnectPanelDisabled: (disabled: boolean) => void
  connectPanelOpen: boolean
  connectPanelDisabled: boolean
} => {
  const { connectPanelOpen, connectPanelDisabled } = useConnectPanelState()
  const dispatch = useConnectPanelDispatch()
  const setConnectPanelOpen = (open: boolean) => {
    dispatch({
      type: ConnectPanelActionType.SET_CONNECT_PANEL_OPEN,
      open
    })
  }

  const setConnectPanelDisabled = (disabled: boolean) => {
    dispatch({
      type: ConnectPanelActionType.SET_CONNECT_PANEL_DISABLED,
      disabled
    })
  }

  return { setConnectPanelOpen, setConnectPanelDisabled, connectPanelOpen, connectPanelDisabled }
}
